import {
  Button, Card, Col, Container, ErrorMessage, Row,
} from 'nhsuk-react-components';
import { useState } from 'react';

import useCampaign from 'hooks/useCampaign.ts';
import CustomReactSelect, {
  Option,
} from 'components/CustomReactSelect/CustomReactSelect.tsx';
import TeamRoutingPlans from 'components/TeamRoutingPlans';
import './RoutingIDs.scss';
import featureFlags from 'services/featureFlags';
import { RoutingPlans } from 'schemas/RoutingPlan.schema';
import {
  findRoutingPlan,
  getLatestRoutingPlans,
  getRoutingPlanId,
  getRoutingPlanName,
  hasOnlyUnversionedRoutingPlans,
} from 'utils/routingPlansUtils';

interface RoutingIDsFormProps {
  onSuccessfulSubmission: (arg0: string) => void;
  existingRoutingIDs: string[];
  teamRoutingPlans: RoutingPlans;
  isSubmitting?: boolean;
}

function RoutingIDsForm({
  onSuccessfulSubmission,
  existingRoutingIDs,
  teamRoutingPlans,
  isSubmitting = false,

}: RoutingIDsFormProps) {
  const { notifyRoutingPlansEnabled } = featureFlags;
  const shouldShowUnversionedRoutingPlans = !notifyRoutingPlansEnabled || hasOnlyUnversionedRoutingPlans(existingRoutingIDs);
  const { campaign } = useCampaign();

  const initialAvailableRoutingIds = campaign.CampaignConfig.AvailableRoutingIds || [];
  const versionedRoutingIds = initialAvailableRoutingIds.filter((id) => id.includes(':'));

  const latestPlans = getLatestRoutingPlans(teamRoutingPlans);
  const resolvedExistingRoutingIDs = existingRoutingIDs.map((id) => {
    if (!id.includes(':')) {
      const latest = latestPlans.find((plan) => plan.routing_plan_id === id);
      if (latest?.version) return `${latest.routing_plan_id}:${latest.version}`;
    }
    return id;
  });

  const [availableRoutingIds, setAvailableRoutingIds] = useState<string[]>(
    initialAvailableRoutingIds.filter(
      (id) => !resolvedExistingRoutingIDs.includes(id),
    ),
  );
  const [currentlySelected, setCurrentlySelected] = useState<string>(
    initialAvailableRoutingIds.find((id) => !resolvedExistingRoutingIDs.includes(id)) || '',
  );
  const [selectedRoutingIds, setSelectedRoutingIds] = useState<string[]>(resolvedExistingRoutingIDs);

  const hasCampaignRoutingIds = initialAvailableRoutingIds.length > 0;

  const campaignRoutingPlanRows = versionedRoutingIds.map(
    (routingID) => findRoutingPlan(routingID, teamRoutingPlans),
  ).filter(Boolean) as RoutingPlans;

  const selectedPlanRows = selectedRoutingIds
    .filter((routingID) => routingID.includes(':'))
    .map(
      (routingID) => findRoutingPlan(routingID, teamRoutingPlans),
    )
    .filter(Boolean) as RoutingPlans;

  const handleSelectRoutingPlan = (routingPlanId: string, version: number | undefined) => {
    const routingId = getRoutingPlanId(routingPlanId, version);

    if (selectedRoutingIds.includes(routingId)) {
      return;
    }

    setSelectedRoutingIds([...selectedRoutingIds, routingId]);

    if (!shouldShowUnversionedRoutingPlans) {
      return;
    }

    setAvailableRoutingIds(
      availableRoutingIds.filter((availableRoutingId) => availableRoutingId !== routingId),
    );
  };

  const handleSelectChange = (selectedOption: Option[] | null) => {
    if (selectedOption && selectedOption.length > 0) {
      setCurrentlySelected(selectedOption[0].value);
    }
  };

  const handleAdd = () => {
    if (
      !currentlySelected
      || selectedRoutingIds.some(
        (selectedRoutingID) => selectedRoutingID === currentlySelected,
      )
    ) {
      return;
    }

    setSelectedRoutingIds([...selectedRoutingIds, currentlySelected]);
    const newSelectedRoutingIds = availableRoutingIds.filter(
      (routingID) => routingID !== currentlySelected,
    );
    setAvailableRoutingIds(newSelectedRoutingIds);
    setCurrentlySelected(
      newSelectedRoutingIds.length > 0 ? newSelectedRoutingIds[0] : '',
    );
  };

  const handleAddAll = () => {
    const newSelectedRoutingIds = [
      ...selectedRoutingIds,
      ...availableRoutingIds,
    ];
    setSelectedRoutingIds(newSelectedRoutingIds);
    setAvailableRoutingIds([]);
    setCurrentlySelected('');
  };

  const handleDelete = (routingId: string) => {
    setSelectedRoutingIds(
      selectedRoutingIds.filter(
        (selectedRoutingID) => selectedRoutingID !== routingId,
      ),
    );

    if (!shouldShowUnversionedRoutingPlans) {
      return;
    }

    setAvailableRoutingIds([...availableRoutingIds, routingId]);
  };

  const onSubmit = () => {
    if (selectedRoutingIds.length === 0) {
      onSuccessfulSubmission('');
    }

    onSuccessfulSubmission(selectedRoutingIds.join('|'));
  };

  return (
    <>
      <Card>
        <>
          {!hasCampaignRoutingIds && (
            <div className="nhsuk-form-group--error">
              <ErrorMessage>No message plans are available for this campaign. You can continue setting up this iteration, but you must add message plans to the campaign before you can send it for review.</ErrorMessage>
              <p className="nhsuk-body-m nhsuk-u-font-weight-bold">No message plans at campaign level</p>
            </div>
          )}
          {hasCampaignRoutingIds && (
            <Container>
              <h4>Select your default message plans</h4>
              <Row className="align-center-row">
                {!shouldShowUnversionedRoutingPlans ? (
                  <Col width="full">
                    <TeamRoutingPlans
                      routingPlans={campaignRoutingPlanRows}
                      selectedRoutingPlans={selectedPlanRows}
                      onSelectPlan={handleSelectRoutingPlan}
                      onRemovePlan={(routingPlanId, version) => handleDelete(getRoutingPlanId(routingPlanId, version))}
                    />
                  </Col>
                ) : (
                  <>
                    <Col width="three-quarters">
                      <CustomReactSelect
                        inputId="message-plans-select"
                        options={availableRoutingIds
                          .map((routingID) => ({
                            value: routingID,
                            label: getRoutingPlanName(teamRoutingPlans, routingID),
                          }))
                          .toSorted((a, b) => a.label.localeCompare(b.label))}
                        onChange={handleSelectChange}
                        value={{
                          value: currentlySelected,
                          label: getRoutingPlanName(teamRoutingPlans, currentlySelected),
                        }}
                        placeholder="Select a message plan"
                      />
                    </Col>
                    <Col width="one-quarter" className="action-buttons-column">
                      <Button
                        className="smaller-button nhsuk-u-margin-right-2"
                        aria-label="Add a Default Message Plan"
                        data-testid="add-button"
                        onClick={handleAdd}
                      >
                        Add
                      </Button>
                      <Button
                        secondary
                        className="smaller-button secondary"
                        aria-label="Add all Default Message Plans"
                        data-testid="add-all-button"
                        onClick={handleAddAll}
                      >
                        Add all
                      </Button>
                    </Col>
                  </>
                )}
              </Row>
            </Container>
          )}
        </>
      </Card>

      {shouldShowUnversionedRoutingPlans && hasCampaignRoutingIds && (
        <Card cardType="non-urgent">
          <Card.Heading>
            <p className="nhsuk-u-margin-bottom-0">
              Campaign message plans
            </p>
          </Card.Heading>
          <div className="nhsuk-u-padding-bottom-1">
            {selectedRoutingIds.length === 0 && (
              <Row>
                <Col width="three-quarters">
                  <p>
                    The message plans you add will appear
                    here.
                  </p>
                </Col>
              </Row>
            )}
            {selectedRoutingIds.map((selectedRoutingID, idx) => (
              <Row
                className="nhsuk-u-padding-top-3 nhsuk-u-padding-bottom-1"
                key={`${selectedRoutingID}-selected`}
              >
                <Col width="three-quarters">
                  <p className="text-wrap">{selectedRoutingID}</p>
                </Col>
                <Col width="one-quarter">
                  <Button
                    secondary
                    className="smaller-button"
                    onClick={() => handleDelete(selectedRoutingID)}
                    data-testid={`${selectedRoutingID}-delete-button`}
                    aria-label={`Delete Message Plan ${idx + 1} ${selectedRoutingID}`}
                  >
                    Delete
                  </Button>

                </Col>
                {idx !== selectedRoutingIds.length - 1 && (
                  <div className="row-border nhsuk-u-padding-top-1 nhsuk-u-margin-left-0" />
                )}
              </Row>
            ))}
          </div>
        </Card>
      )}

      <Button onClick={onSubmit} disabled={isSubmitting}>
        {isSubmitting ? 'Updating...' : 'Save and continue'}
      </Button>
    </>
  );
}

export default RoutingIDsForm;
